import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Modal from '../../../components/ui/Modal';
import { buyerRepresentationContractService } from '../../../services/buyerRepresentationContractService';
import { generateBuyerRepresentationContractTemplate } from '../../../services/openaiContractTemplateService';
import { contractValidation, realtimeValidation } from '../../../utils/validationUtils';
import ContractPreviewModal from './ContractPreviewModal';

const BuyerRepresentationContractModal = ({ isOpen, onClose, contractId, contractAmount, onSuccess }) => {
  const [formData, setFormData] = useState({
    arddjoRetainerPercentage: 4.0,
    clientRetainerPercentage: 2.0,
    representationDurationMonths: 6,
    exclusiveRepresentation: true,
    propertyTypes: ['apartment'],
    budgetMin: contractAmount ? contractAmount * 0.8 : 0,
    budgetMax: contractAmount || 0,
    preferredLocations: [],
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedAmounts, setCalculatedAmounts] = useState(null);
  const [locationInput, setLocationInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContract, setGeneratedContract] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFullPreviewModal, setShowFullPreviewModal] = useState(false);

  const propertyTypeOptions = [
    { value: 'apartment', label: 'شقة' },
    { value: 'villa', label: 'فيلا' },
    { value: 'townhouse', label: 'تاون هاوس' },
    { value: 'land', label: 'أرض' },
    { value: 'commercial', label: 'تجاري' },
    { value: 'office', label: 'مكتب' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Real-time validation
    const error = realtimeValidation?.validateField(field, value, { ...formData, [field]: value });
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    const value = formData?.[field];
    const isRequired = ['arddjoRetainerPercentage', 'clientRetainerPercentage']?.includes(field);
    const error = realtimeValidation?.validateOnBlur(field, value, isRequired);
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // CLIENT-SIDE VALIDATION: Clear error for this field
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Recalculate amounts when percentages change
    if (field === 'arddjoRetainerPercentage' || field === 'clientRetainerPercentage') {
      const amounts = buyerRepresentationContractService?.calculateRetainerAmounts(
        contractAmount,
        field === 'arddjoRetainerPercentage' ? parseFloat(value) : formData?.arddjoRetainerPercentage,
        field === 'clientRetainerPercentage' ? parseFloat(value) : formData?.clientRetainerPercentage
      );
      setCalculatedAmounts(amounts);
    }
  };

  const handlePropertyTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      propertyTypes: prev?.propertyTypes?.includes(type)
        ? prev?.propertyTypes?.filter(t => t !== type)
        : [...prev?.propertyTypes, type]
    }));
  };

  const handleAddLocation = () => {
    if (locationInput?.trim()) {
      setFormData(prev => ({
        ...prev,
        preferredLocations: [...prev?.preferredLocations, locationInput?.trim()]
      }));
      setLocationInput('');
    }
  };

  const handleRemoveLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      preferredLocations: prev?.preferredLocations?.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // CLIENT-SIDE VALIDATION: Validate before submitting
    const validation = contractValidation?.validateBuyerContract({
      ...formData,
      contractId,
      amount: contractAmount
    });
    
    if (!validation?.isValid) {
      setValidationErrors(validation?.errors);
      
      // Show error message
      const firstError = Object.values(validation?.errors)?.[0];
      alert(`خطأ في البيانات: ${firstError}`);
      return;
    }

    setIsSubmitting(true);

    try {
      await buyerRepresentationContractService?.createBuyerRepresentationContract({
        contractId,
        ...formData
      });

      alert('تم إنشاء عقد تمثيل المشتري بنجاح');
      setValidationErrors({});
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating buyer representation contract:', error);
      
      // Handle specific error types
      let errorMessage = 'فشل في إنشاء عقد تمثيل المشتري';
      
      if (error?.message?.includes('invalid input syntax for type uuid')) {
        errorMessage = 'خطأ في معرف العقد. يرجى إنشاء العقد الأساسي أولاً من خلال نموذج العقد الجديد.';
      } else if (error?.message?.includes('تجاوز حد استخدام')) {
        errorMessage = error?.message;
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateWithAI = async () => {
    setIsGenerating(true);
    try {
      const template = await generateBuyerRepresentationContractTemplate({
        clientName: 'اسم العميل',
        budgetMin: formData?.budgetMin,
        budgetMax: formData?.budgetMax,
        arddjoRetainerPercentage: formData?.arddjoRetainerPercentage,
        clientRetainerPercentage: formData?.clientRetainerPercentage,
        representationDurationMonths: formData?.representationDurationMonths,
        exclusiveRepresentation: formData?.exclusiveRepresentation,
        propertyTypes: formData?.propertyTypes,
        preferredLocations: formData?.preferredLocations
      });
      
      console.log('AI Generated Template:', template);
      
      // Store the generated contract and show preview
      setGeneratedContract(template);
      setShowPreview(true);
      
      alert('تم إنشاء قالب العقد بنجاح باستخدام الذكاء الاصطناعي. يمكنك الآن معاينة العقد وحفظه.');
    } catch (error) {
      console.error('Error generating template:', error);
      
      // Show user-friendly error message
      let errorMessage = 'فشل في إنشاء قالب العقد';
      
      if (error?.message?.includes('تجاوز حد استخدام')) {
        errorMessage = error?.message;
      } else if (error?.message?.includes('خطأ في مفتاح API')) {
        errorMessage = error?.message;
      } else if (error?.message?.includes('غير متاحة حالياً')) {
        errorMessage = error?.message;
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  React.useEffect(() => {
    if (contractAmount) {
      const amounts = buyerRepresentationContractService?.calculateRetainerAmounts(
        contractAmount,
        formData?.arddjoRetainerPercentage,
        formData?.clientRetainerPercentage
      );
      setCalculatedAmounts(amounts);
    }
  }, [contractAmount, formData?.arddjoRetainerPercentage, formData?.clientRetainerPercentage]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="عقد تمثيل مشتري عقاري" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Retainer Percentages */}
        <div className="bg-muted rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Percent" size={16} />
            نسب العمولة المرنة
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نسبة عمولة ARDDJO (%)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData?.arddjoRetainerPercentage}
                onChange={(e) => handleChange('arddjoRetainerPercentage', e?.target?.value)}
                onBlur={() => handleBlur('arddjoRetainerPercentage')}
                required
              />
              {calculatedAmounts && (
                <p className="text-xs text-muted-foreground mt-1">
                  المبلغ: {calculatedAmounts?.arddjoRetainerAmount?.toLocaleString('ar-JO')} دينار
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نسبة عمولة العميل (%)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData?.clientRetainerPercentage}
                onChange={(e) => handleChange('clientRetainerPercentage', e?.target?.value)}
                onBlur={() => handleBlur('clientRetainerPercentage')}
                required
              />
              {calculatedAmounts && (
                <p className="text-xs text-muted-foreground mt-1">
                  المبلغ: {calculatedAmounts?.clientRetainerAmount?.toLocaleString('ar-JO')} دينار
                </p>
              )}
            </div>
          </div>
          {calculatedAmounts && (
            <div className="mt-4 p-3 bg-primary/10 rounded-lg">
              <p className="text-sm font-semibold text-primary">
                إجمالي العمولة: {calculatedAmounts?.totalRetainerAmount?.toLocaleString('ar-JO')} دينار
              </p>
            </div>
          )}
        </div>

        {/* Representation Details */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              مدة التمثيل (أشهر)
            </label>
            <Input
              type="number"
              min="1"
              max="24"
              value={formData?.representationDurationMonths}
              onChange={(e) => handleInputChange('representationDurationMonths', e?.target?.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                الحد الأدنى للميزانية (دينار)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData?.budgetMin}
                onChange={(e) => handleInputChange('budgetMin', e?.target?.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                الحد الأقصى للميزانية (دينار)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData?.budgetMax}
                onChange={(e) => handleInputChange('budgetMax', e?.target?.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              التمثيل الحصري
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData?.exclusiveRepresentation === true}
                  onChange={() => handleInputChange('exclusiveRepresentation', true)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">نعم - حصري</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData?.exclusiveRepresentation === false}
                  onChange={() => handleInputChange('exclusiveRepresentation', false)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">لا - غير حصري</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              أنواع العقارات المطلوبة
            </label>
            <div className="grid grid-cols-2 gap-2">
              {propertyTypeOptions?.map(option => (
                <label
                  key={option?.value}
                  className="flex items-center gap-2 p-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData?.propertyTypes?.includes(option?.value)}
                    onChange={() => handlePropertyTypeToggle(option?.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">{option?.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              المواقع المفضلة
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                value={locationInput}
                onChange={(e) => setLocationInput(e?.target?.value)}
                placeholder="أدخل الموقع"
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    handleAddLocation();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddLocation}
              >
                <Icon name="Plus" size={16} />
              </Button>
            </div>
            {formData?.preferredLocations?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData?.preferredLocations?.map((location, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-muted border border-border rounded-full text-xs font-medium text-foreground flex items-center gap-2"
                  >
                    {location}
                    <button
                      type="button"
                      onClick={() => handleRemoveLocation(idx)}
                      className="hover:text-error"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              ملاحظات إضافية
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="أدخل أي ملاحظات أو متطلبات خاصة..."
            />
          </div>
        </div>

        {/* Auto-Generate Contract Section */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <Icon name="Sparkles" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-1">
                إنشاء نموذج العقد تلقائياً
              </h4>
              <p className="text-xs text-muted-foreground">
                استخدم OpenAI لإنشاء نموذج عقد تمثيل مشتري احترافي بناءً على البيانات المدخلة مع الحفاظ على تنسيق RTL العربي
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={handleGenerateWithAI}
            disabled={isGenerating}
            className="w-full"
            variant="outline"
          >
            {isGenerating ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <Icon name="FileText" size={16} />
                إنشاء نموذج العقد بالذكاء الاصطناعي
              </>
            )}
          </Button>
        </div>

        {/* Contract Preview */}
        {showPreview && generatedContract && (
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Eye" size={16} />
                معاينة العقد المُنشأ
              </h4>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => setShowFullPreviewModal(true)}
                  variant="outline"
                  size="sm"
                >
                  <Icon name="Maximize2" size={14} className="ml-1" />
                  معاينة كاملة وطباعة
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  variant="ghost"
                  size="sm"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
            <div 
              className="max-h-96 overflow-y-auto border border-border rounded-lg p-4 bg-background"
              dir="rtl"
              dangerouslySetInnerHTML={{ __html: generatedContract?.htmlContent }}
            />
            <div className="mt-3 text-xs text-muted-foreground">
              <p>تم الإنشاء بواسطة: {generatedContract?.metadata?.model}</p>
              <p>التاريخ: {new Date(generatedContract?.metadata?.generatedAt)?.toLocaleString('ar-JO')}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            variant="default"
            fullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Icon name="Loader" size={16} className="animate-spin ml-2" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <Icon name="Check" size={16} className="ml-2" />
                إنشاء عقد التمثيل
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            إلغاء
          </Button>
        </div>
      </form>
      {/* Full Preview Modal */}
      {generatedContract && (
        <ContractPreviewModal
          isOpen={showFullPreviewModal}
          onClose={() => setShowFullPreviewModal(false)}
          contract={{
            ...formData,
            generated_contract: generatedContract?.htmlContent,
            id: contractId,
            created_at: new Date()?.toISOString()
          }}
          contractType="buyer_representation"
        />
      )}
    </Modal>
  );
};

export default BuyerRepresentationContractModal;